import _ from 'lodash';
import { SmartenitPlugin } from '../smartenit-plugin';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import { ITextValue } from '../../interfaces/text-value.interface';
import { INumericValue } from '../../interfaces/numeric-value.interface';
import { IOnOff } from '../../interfaces/on-off.interface';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';

export class MeteringOnOffServerPlugin extends SmartenitPlugin
  implements ITextValue, INumericValue, IOnOff {
  private _timerEnabled: boolean = false;
  private _timerDurationInSeconds: number = 0;

  public getReportableAttrs() {
    return ['SummationDelivered', 'Active', 'Voltage', 'Current', 'OnOff', 'RunningTimer', 'Mode'];
  }

  public on(): void {
    this.state.onOff = true;
    return this.executeMethod(this.componentId, this.processorName, 'On', null);
  }

  public off(): void {
    this.state.onOff = false;
    return this.executeMethod(
      this.componentId,
      this.processorName,
      'Off',
      null
    );
  }

  public setTimerEnabled(enable: boolean): void {
    this._timerEnabled = enable;
    const params = { value: enable };
    this.device
      .setAttribute(this.componentId, this.processorName, 'TimerEnabled', params, false)
      .subscribe();
    this._onUpdate.next({ _timerEnabled: this._timerEnabled });
  }

  public updateTimerEnabled(): void {
    this.device
      .getAttribute(
        this.componentId,
        this.processorName,
        'TimerEnabled',
        undefined,
        'cached=false'
      )
      .subscribe();
  }

  public setTimer(timeInSeconds: number) {
    this._timerDurationInSeconds = timeInSeconds;
    const params = { value: timeInSeconds * 10 }; // Zigbee expects values in 1/10 of a seconds
    this.device
      .setAttribute(this.componentId, this.processorName, 'OnTime', params, false)
      .subscribe();
    this._onUpdate.next({ compNumber: this.componentId, _timerDurationInSeconds: this._timerDurationInSeconds });
  }

  public updateTimer(): void {
    this.device
      .getAttribute(
        this.componentId,
        this.processorName,
        'OnTime',
        undefined,
        'cached=false'
      )
      .subscribe();
  }

  public toggle(): void {
    // return this.executeMethod(this.componentId, this.processorName, 'Toggle', null);
    if (this.isOn()) {
      this.off();
    } else {
      this.on();
    }
  }

  public isOn(): boolean {
    return this.state.onOff === true;
  }

  public isOff(): boolean {
    return this.state.onOff === false;
  }

  public isTimerEnabled(): boolean {
    return this._timerEnabled;
  }

  public getTimerDurationInSeconds(): number {
    return this._timerDurationInSeconds;
  }

  public getUnit(attribute?: string): string {
    let response: string = '';

    switch (attribute) {
      case 'ActivePower':
        response = 'kW';
        break;
      case 'SummationDelivered':
        response = 'kWh';
        break;
    }

    return response;
  }

  public getActivePower(): string {
    return this.state.activePower != null && this.state.acPowerMultiplier != null && this.state.acPowerDivisor != null
      ? !isNaN(Number(this.state.activePower)) && !isNaN(Number(this.state.acPowerMultiplier)) && !isNaN(Number(this.state.acPowerDivisor))
        ? ((this.state.activePower * (this.state.acPowerMultiplier / this.state.acPowerDivisor)) / 1000).toFixed(2)
        : '--'
      : '--';
  }

  public getSummationDelivered(): string {
    return this.state.summationDelivered != null && this.state.summationDeliveredMultiplier != null && this.state.summationDeliveredDivisor != null
      ? !isNaN(Number(this.state.summationDelivered)) && !isNaN(Number(this.state.summationDeliveredMultiplier))
        && !isNaN(Number(this.state.summationDeliveredDivisor))
        ? (((this.state.summationDelivered * (this.state.summationDeliveredMultiplier / this.state.summationDeliveredDivisor)) / 1000)
          .toFixed(3)).toString()
        : '--'
      : '--';
  }

  public getValue(attribute?: string): number {
    let response: number = 0;

    switch (attribute) {
      case 'ActivePower':
        response = this.state.activePower != null && this.state.acPowerMultiplier != null
          && this.state.acPowerDivisor != null
          ? !isNaN(Number(this.state.activePower)) && !isNaN(Number(this.state.acPowerMultiplier))
            && !isNaN(Number(this.state.acPowerDivisor))
            ? Number(((this.state.activePower * (this.state.acPowerMultiplier / this.state.acPowerDivisor)) / 1000)
              .toFixed(3))
            : 0
          : 0;
        break;
      case 'ActivePowerValue':
        response = this.state.activePowerValue != null
          ? !isNaN(Number(this.state.activePowerValue))
            ? Number((Number(this.state.activePowerValue)).toFixed(3))
            : 0
          : 0;
        break;
      case 'SummationDelivered':
        response = this.state.summationDelivered != null && this.state.summationDeliveredMultiplier != null
          && this.state.summationDeliveredDivisor != null
          ? !isNaN(Number(this.state.summationDelivered)) && !isNaN(Number(this.state.summationDeliveredMultiplier))
            && !isNaN(Number(this.state.summationDeliveredDivisor))
            ? Number(((this.state.summationDelivered * (this.state.summationDeliveredMultiplier / this.state.summationDeliveredDivisor)) / 1000)
              .toFixed(3))
            : 0
          : 0;
        break;
    }

    return response;
  }

  public getConditionAttribute(): string {
    // return this by default but client can choose the attribute from JSON
    return 'ActivePower';
  }

  public getConditionOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.IS_ON',
            ''
          )
        ),
        name: 'is On',
        value: 1
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.IS_OFF',
            ''
          )
        ),
        name: 'is Off',
        value: 0
      }
    ];
  }

  public getEffectOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_ON',
            ''
          )
        ),
        name: 'turn On',
        value: {
          method: 'On'
        }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_OFF',
            ''
          )
        ),
        name: 'turn Off',
        value: {
          method: 'Off'
        }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TOGGLE',
            ''
          )
        ),
        name: 'Toggle',
        value: {
          method: 'Toggle'
        }
      }
    ];
  }

  public getEffectAttribute(): string | null {
    return null;
  }

  public getEffectMethod(context?: any): string | null {
    if (!context) {
      return null;
    }

    const currentMethod = context.method;
    if (['On', 'Off', 'Toggle'].indexOf(currentMethod) > -1) {
      return currentMethod;
    }

    return null;
  }

  public getResponderOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_ON',
            ''
          )
        ),
        name: 'turn On',
        value: true
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.TURN_OFF',
            ''
          )
        ),
        name: 'turn Off',
        value: false
      }
    ];
  }

  public getResponderAttribute(): string {
    return 'OnOff';
  }

  public getResponderMethod(): string | null {
    return null;
  }

  public setValue(value: number, attribute?: string, subscribe?: boolean): any {
    // Do nothing becase values are read only

    if (subscribe) {
      return Observable.of(true);
    }

    return true;
  }

  public getTextValue(context?: string): string {
    if (context && context == 'displayActivePowerOnly') {
      return this.getActivePower() + this.getUnit('ActivePower');
    } else {
      return `${this.getActivePower()} kW / ${this.getSummationDelivered()} kWh`;
    }
  }

  public formatValue(value: string): string {
    return value;
  }

  public getStatusPayload(): any {
    return [
      'OnOff',
      'ActivePower',
      'SummationDelivered',
      'ACPowerMultiplier',
      'ACPowerDivisor',
      'SummationDeliveredMultiplier',
      'SummationDeliveredDivisor'
    ];
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const response = message && message.data && message.data.response;
    const attribute = message && message.attributeOrMethod;

    if (attribute === 'OnOff') {
      this.state.onOff = response.value;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ActivePower') {
      this.state.activePower = response.value;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ActivePowerValue') {
      this.state.activePowerValue = response.value;
    } else if (attribute === 'SummationDelivered') {
      this.state.summationDelivered = response.value;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ACPowerMultiplier') {
      this.state.acPowerMultiplier = response.value;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ACPowerDivisor') {
      this.state.acPowerDivisor = response.value;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'SummationDeliveredMultiplier') {
      this.state.summationDeliveredMultiplier = response.value;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'SummationDeliveredDivisor') {
      this.state.summationDeliveredDivisor = response.value;
      this._onUpdate.next({ state: this.state });
    }

    return this.state;
  }
}
